/**
 * Cronbach's alpha and the item statistics that come with it.
 *
 * Input is a complete n × k matrix — respondents × items, missing rows already removed
 * (listwise deletion is the caller's, so it can say how many rows it dropped).
 *
 * Variances are sample variances (n − 1), as SPSS and R's `psych::alpha` report them.
 * Alpha itself does not depend on the choice — it is a ratio of variances — but the item
 * SDs printed beside it do.
 */

import { mean, pearson, variance } from "@/lib/stats/descriptive";
import { fQuantile } from "@/lib/stats/distributions";

export interface ItemStatistics {
  mean: number;
  sd: number;
  /** Corrected item–total correlation: the item against the sum of the OTHER items. */
  itemRest: number;
  /** Alpha of the remaining items; null when only two items exist. */
  alphaIfDeleted: number | null;
}

export interface AlphaResult {
  n: number;
  k: number;
  alpha: number;
  /** From the mean inter-item correlation; NaN when an item has no variance. */
  standardized: number;
  meanInterItem: number;
  itemVarianceSum: number;
  totalVariance: number;
  totalMean: number;
  totalSd: number;
  /** Feldt's (1965) F-based 95% interval. */
  lower: number;
  upper: number;
  items: ItemStatistics[];
}

function columnsOf(
  rows: readonly (readonly number[])[],
  k: number
): number[][] {
  return Array.from({ length: k }, (_, j) => rows.map((row) => row[j]));
}

function rowTotals(columns: readonly number[][]): number[] {
  const n = columns[0]?.length ?? 0;
  const totals = new Array<number>(n).fill(0);
  for (const column of columns) {
    for (let i = 0; i < n; i++) {
      totals[i] += column[i];
    }
  }
  return totals;
}

/** α = k/(k − 1) · (1 − Σ s²ᵢ / s²ₜ). NaN when the totals have no variance. */
export function alphaFromColumns(columns: readonly number[][]): number {
  const k = columns.length;
  if (k < 2) {
    return Number.NaN;
  }
  let itemVariance = 0;
  for (const column of columns) {
    itemVariance += variance(column);
  }
  const totalVariance = variance(rowTotals(columns));
  if (!(totalVariance > 0)) {
    return Number.NaN;
  }
  return (k / (k - 1)) * (1 - itemVariance / totalVariance);
}

/**
 * Feldt's interval: (1 − α)/(1 − α̂) follows F with n − 1 and (n − 1)(k − 1) degrees of
 * freedom, so the bounds are 1 − (1 − α̂)·F at the upper and lower tail points.
 */
export function feldtInterval(
  alpha: number,
  n: number,
  k: number,
  level = 0.95
): { lower: number; upper: number } {
  const df1 = n - 1;
  const df2 = (n - 1) * (k - 1);
  const tail = (1 - level) / 2;
  return {
    lower: 1 - (1 - alpha) * fQuantile(1 - tail, df1, df2),
    upper: 1 - (1 - alpha) * fQuantile(tail, df1, df2),
  };
}

function meanInterItemCorrelation(columns: readonly number[][]): number {
  let total = 0;
  let pairs = 0;
  for (let a = 0; a < columns.length; a++) {
    for (let b = a + 1; b < columns.length; b++) {
      total += pearson(columns[a], columns[b]);
      pairs += 1;
    }
  }
  return pairs === 0 ? Number.NaN : total / pairs;
}

function itemStatistics(
  columns: readonly number[][],
  totals: readonly number[]
): ItemStatistics[] {
  return columns.map((column, j) => {
    const rest = totals.map((total, i) => total - column[i]);
    const others = columns.filter((_, other) => other !== j);
    return {
      mean: mean(column),
      sd: Math.sqrt(variance(column)),
      itemRest: pearson(column, rest),
      alphaIfDeleted: others.length >= 2 ? alphaFromColumns(others) : null,
    };
  });
}

/** Everything the calculator reports, or null when α is undefined for this data. */
export function cronbachAlpha(
  rows: readonly (readonly number[])[]
): AlphaResult | null {
  const n = rows.length;
  const k = rows[0]?.length ?? 0;
  if (n < 2 || k < 2) {
    return null;
  }
  const columns = columnsOf(rows, k);
  const alpha = alphaFromColumns(columns);
  if (!Number.isFinite(alpha)) {
    return null;
  }
  const totals = rowTotals(columns);
  const meanR = meanInterItemCorrelation(columns);
  const { lower, upper } = feldtInterval(alpha, n, k);
  let itemVarianceSum = 0;
  for (const column of columns) {
    itemVarianceSum += variance(column);
  }
  return {
    n,
    k,
    alpha,
    standardized: (k * meanR) / (1 + (k - 1) * meanR),
    meanInterItem: meanR,
    itemVarianceSum,
    totalVariance: variance(totals),
    totalMean: mean(totals),
    totalSd: Math.sqrt(variance(totals)),
    lower,
    upper,
    items: itemStatistics(columns, totals),
  };
}

/** George & Mallery's (2003) rule of thumb — a convention, not a test. */
export type AlphaBand =
  | "excellent"
  | "good"
  | "acceptable"
  | "questionable"
  | "poor"
  | "unacceptable";

export function alphaBand(alpha: number): AlphaBand {
  if (alpha >= 0.9) {
    return "excellent";
  }
  if (alpha >= 0.8) {
    return "good";
  }
  if (alpha >= 0.7) {
    return "acceptable";
  }
  if (alpha >= 0.6) {
    return "questionable";
  }
  if (alpha >= 0.5) {
    return "poor";
  }
  return "unacceptable";
}

/** Reverse-score a value on a scale from `low` to `high`: (low + high) − x. */
export function reverseScore(value: number, low: number, high: number): number {
  return low + high - value;
}

export interface ScoredRows {
  rows: number[][];
  /** Rows dropped because a selected item was missing (listwise deletion). */
  dropped: number;
}

/**
 * Keep the selected items, reverse the flagged ones, and drop every row with a missing
 * value among them.
 */
export function prepareItems(
  matrix: readonly (readonly (number | null)[])[],
  include: readonly boolean[],
  reverse: readonly boolean[],
  low: number,
  high: number
): ScoredRows {
  const selected = include.flatMap((on, j) => (on ? [j] : []));
  const rows: number[][] = [];
  let dropped = 0;
  for (const row of matrix) {
    const values = selected.map((j) => row[j] ?? null);
    if (values.some((value) => value === null)) {
      dropped += 1;
      continue;
    }
    rows.push(
      selected.map((j, position) => {
        const value = values[position] as number;
        return reverse[j] ? reverseScore(value, low, high) : value;
      })
    );
  }
  return { rows, dropped };
}
